import { useCallback, useState } from 'react';
import { humanizeApiError, toDateKey, type PodcastConfig, type PodcastRunView } from '@lapo/core';
import type { PodcastSectionState, PodcastSubmitInput, ToastInput } from '@lapo/ui';
import type { ApiClient } from './auth/apiClient.js';
import type { AuthSession } from './auth/provider.js';
import { API_URL } from './config.js';
import { useIntegrationConfig } from './useIntegrationConfig.js';

/**
 * Owns Podcast Summaries (SCRIBE) — the Integrations → Podcast Summaries settings block: the config
 * (keys/defaults), the recent-runs list that rides along with the config load (and refreshes after
 * each submit), the batch "Summarize" run action, and the in-editor `/podcast <url>` slash handler.
 * Behavior-preserving extraction from App.tsx: the recent-runs refresh piggybacks the config `load`,
 * the submit toggles `podcastSubmitting` around `api.runPodcasts` and re-pulls the runs on success,
 * and the slash handler drops results into the caret placeholder then refreshes.
 *
 * `useIntegrationConfig` (the shared Integrations-hub state machine) lives inside the hook — its
 * `enabled` (App's `integrationsEnabled` = `!!session && !!API_URL`) and `visible` (App's
 * `settingsOpen`) gates are passed in. Other deps are `api`, `session`, and `pushToast` — never
 * reached out of App. `session` null (signed out) keeps `podcasts` undefined.
 */
export interface UsePodcastsDeps {
  api: ApiClient;
  session: AuthSession | null;
  pushToast: (t: ToastInput) => number;
  /** Master gate for the config load — App passes `!!session && !!API_URL`. */
  integrationsEnabled: boolean;
  /** Settings visibility — every rising edge triggers a background config refresh (App's `settingsOpen`). */
  settingsOpen: boolean;
}

export interface UsePodcasts {
  /** The Settings-modal section object (undefined until config loads / signed out). */
  podcasts: PodcastSectionState | undefined;
  /** Persist config (from the shared integration-config state machine). */
  onSavePodcasts: (next: PodcastConfig) => Promise<void>;
  /** Submit a batch podcast-summary run. */
  onSubmitPodcasts: (input: PodcastSubmitInput) => Promise<void>;
  /** In-editor `/podcast <url>` — fill the caret placeholder in place. */
  onEditorSummarizePodcast: (url: string, itemId: string) => Promise<boolean>;
}

export function usePodcasts({
  api,
  session,
  pushToast,
  integrationsEnabled,
  settingsOpen,
}: UsePodcastsDeps): UsePodcasts {
  // Podcast Summaries (SCRIBE): load config + recent runs; save keys/defaults; submit a batch run.
  // The recent-runs list rides along with the config load (and refreshes after each submit).
  const [podcastRuns, setPodcastRuns] = useState<PodcastRunView[]>([]);
  const [podcastSubmitting, setPodcastSubmitting] = useState(false);
  const refreshPodcastRuns = useCallback(() => {
    void api.listPodcastRuns(10).then((r) => setPodcastRuns(r.runs)).catch(() => {});
  }, [api]);
  const podcastState = useIntegrationConfig<PodcastConfig>({
    enabled: integrationsEnabled,
    visible: settingsOpen,
    load: () => {
      refreshPodcastRuns();
      return api.getPodcastConfig();
    },
    save: async (next) => {
      const saved = await api.putPodcastConfig(next);
      pushToast({ tone: 'success', message: 'Podcast settings saved.' });
      return saved;
    },
    onSaveError: (err) => pushToast({ tone: 'error', title: 'Could not save', message: err.message }),
  });
  const setPodcastError = podcastState.setError;
  const onSubmitPodcasts = useCallback(
    async (input: PodcastSubmitInput) => {
      setPodcastSubmitting(true);
      setPodcastError(undefined);
      try {
        await api.runPodcasts({
          instruction: input.instruction,
          blob: input.blob,
          detail: input.detail,
          audience: input.audience,
          enrichImages: input.enrichImages,
          target: input.target === 'journal' ? { kind: 'journal', dateKey: toDateKey(new Date()) } : { kind: input.target },
        });
        pushToast({ tone: 'success', message: 'Summarizing — results will appear below when ready.' });
        refreshPodcastRuns();
      } catch (err) {
        setPodcastError(humanizeApiError(err));
        pushToast({ tone: 'error', title: 'Could not submit', message: humanizeApiError(err) });
      } finally {
        setPodcastSubmitting(false);
      }
    },
    [api, pushToast, refreshPodcastRuns, setPodcastError],
  );
  const podcasts: PodcastSectionState | undefined =
    session && API_URL && podcastState.config
      ? { config: podcastState.config, runs: podcastRuns, saving: podcastState.saving, submitting: podcastSubmitting, error: podcastState.error }
      : undefined;
  // In-editor `/podcast <url>`: the editor dropped the `podcast-<itemId>` placeholder at the caret, so
  // we submit with place='client' + the client id, and the worker fills that section in place (D12).
  const onEditorSummarizePodcast = useCallback(
    async (url: string, itemId: string): Promise<boolean> => {
      try {
        await api.slashPodcast(url, { dateKey: toDateKey(new Date()), itemId });
        refreshPodcastRuns();
        return true;
      } catch (err) {
        pushToast({ tone: 'error', title: 'Could not summarize', message: humanizeApiError(err) });
        return false;
      }
    },
    [api, pushToast, refreshPodcastRuns],
  );

  return {
    podcasts,
    onSavePodcasts: podcastState.save,
    onSubmitPodcasts,
    onEditorSummarizePodcast,
  };
}
